/**
 * Retirer les rues qui ne servent plus à rien — **logique pure** (ROADMAP 5.2).
 *
 * ══ Le défaut ═══════════════════════════════════════════════════════════════
 *
 * Le réseau se trace tout seul à chaque pose, et il ne se rétracte jamais. Vendre un
 * bâtiment, en déplacer un autre, et il reste des morceaux de bitume qui ne desservent
 * plus personne. Or `canPlace` refuse de bâtir sur une route : chaque segment orphelin
 * est du **terrain gelé définitivement**, et sur une île saturée c'est du terrain perdu
 * sans recours. `removeRoad` existait déjà, case par case — il manquait de savoir
 * LESQUELLES, et de pouvoir le faire d'un geste.
 *
 * ══ POURQUOI AUCUN REMBOURSEMENT ═════════════════════════════════════════════
 *
 * La ROADMAP prévoyait « un remboursement partiel ». Mesuré dans le code : **une rue ne
 * coûte rien** — `placeBuilding` ne débite que le prix du bâtiment, le tracé est offert.
 * Rembourser un bien gratuit, c'est frapper de la monnaie : poser un bâtiment loin du
 * réseau pour se faire tracer dix cases, les revendre, recommencer. La pompe est
 * immédiate et sans plafond.
 *
 * On ne rembourse donc rien, et le geste se justifie tout seul : il rend du **terrain**,
 * qui est précisément la ressource rare de la fin d'ère.
 *
 * ══ POURQUOI ON N'ÉCRIT PAS LA RÈGLE DE SUPPRESSION ══════════════════════════
 *
 * `removeRoad` sait déjà refuser ce qui enclaverait un bâtiment ou couperait le réseau
 * en deux. On ne réimplémente pas ce jugement ici : on le RAPPELLE, case par case, et on
 * garde ce qu'il accepte. L'invariant de connexité (1 composante, 0 case isolée —
 * `tests/roadNetwork.test.ts`) tient donc **après** chaque suppression par construction,
 * et non parce qu'on aurait pensé à le vérifier. Deux copies d'une même règle ont déjà
 * divergé une fois dans ce dépôt (règle 22).
 */

import { activeIsland, footprintCells, removeRoad } from './gameState';
import type { CitySnapshot } from './gameState';

export interface RoadCleanupResult {
  readonly snapshot: CitySnapshot;
  /** Cases de rue réellement retirées. */
  readonly removed: readonly string[];
}

/** Les quatre voisins d'une case — l'adjacence qui définit « desservir ». */
const NEIGHBOURS = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
] as const;

/**
 * Cases de rue qui ne touchent AUCUNE emprise de bâtiment.
 *
 * Premier filtre, et il est là pour le coût : appeler `removeRoad` sur les deux cents
 * cases d'un réseau relancerait autant de parcours de connexité. L'adjacence se calcule
 * une fois et écarte l'essentiel — ce qui reste est court.
 *
 * ⚠️ Ce filtre ne DÉCIDE de rien : une case sans bâtiment adjacent peut très bien être
 * le seul passage entre deux quartiers. C'est `removeRoad` qui tranche.
 */
function unservedRoads(snapshot: CitySnapshot): readonly string[] {
  const island = activeIsland(snapshot);

  const served = new Set<string>();
  for (const building of island.buildings) {
    for (const cell of footprintCells(building.type, building.col, building.row)) {
      for (const [dCol, dRow] of NEIGHBOURS) {
        served.add(`${String(cell.col + dCol)}:${String(cell.row + dRow)}`);
      }
    }
  }

  return island.roads.filter((key) => !served.has(key));
}

/**
 * Retire tout le bitume orphelin qu'il est possible de retirer.
 *
 * ⚠️ **Plusieurs passes, et ce n'est pas de la prudence.** Une impasse de trois cases se
 * dénoue par le bout : la case du fond part, ce qui rend la suivante retirable, et ainsi
 * de suite. Une seule passe ne retirerait que le premier maillon de chaque chaîne, et le
 * joueur devrait rappuyer sur le bouton jusqu'à ce que ça ne bouge plus — un geste qu'on
 * lui fait répéter est un geste qu'on n'a pas fini.
 *
 * La boucle s'arrête quand une passe entière n'a rien retiré : elle termine donc
 * toujours, le nombre de cases ne pouvant que décroître.
 */
export function cleanupOrphanRoads(snapshot: CitySnapshot, now: number): RoadCleanupResult {
  let working = snapshot;
  const removed: string[] = [];

  for (;;) {
    let progressed = false;

    for (const key of unservedRoads(working)) {
      const [col, row] = key.split(':').map(Number);
      if (col === undefined || row === undefined) continue;

      const attempt = removeRoad(working, col, row, now);
      if (!attempt.ok) continue;

      working = attempt.snapshot;
      removed.push(key);
      progressed = true;
    }

    if (!progressed) break;
  }

  return { snapshot: removed.length === 0 ? snapshot : working, removed };
}

/**
 * Combien de cases le nettoyage rendrait — **sans rien modifier**.
 *
 * Le compte passe par la simulation complète plutôt que par une estimation : un chiffre
 * annoncé qui diffère du geste est pire que pas de chiffre du tout. C'est le même
 * principe que `quoteTopUp` — l'interface montre ce que l'action fera, pas ce qu'on
 * suppose qu'elle fera.
 */
export function orphanRoadCount(snapshot: CitySnapshot, now: number): number {
  return cleanupOrphanRoads(snapshot, now).removed.length;
}
